import React, { useState, useEffect } from 'react';
import { useHistory, Link as RouterLink, useLocation } from 'react-router-dom';
import { 
  Button, 
  TextField, 
  Checkbox, 
  FormControlLabel, 
  Typography, 
  Container, 
  Box,
  Alert,
  Paper,
  Link,
  Avatar
} from '@mui/material';
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

function Login() {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fromPayment, setFromPayment] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const { login } = useAuth();

  useEffect(() => {
    // Check for payment_status in URL parameters
    const searchParams = new URLSearchParams(location.search);
    const paymentStatus = searchParams.get('payment_status');
    
    if (paymentStatus === 'success') {
      setSuccess('Payment initiated successfully! Please log in to access your account. Your credits will be added to your account after the payment is confirmed.');
      // Set flag to redirect to credits page after login
      setFromPayment(true);
      
      // Remove the parameter from URL but keep the history clean
      const newUrl = location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    } else if (paymentStatus === 'cancelled') {
      setError('Your payment was cancelled. If you still wish to purchase credits, please log in and try again.');
      // Remove the parameter from URL but keep the history clean
      const newUrl = location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [location, history]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'rememberMe' ? checked : value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);
    
    try {
      await login(formData.username, formData.password, formData.rememberMe);
      // Redirect to credits page if coming from payment, otherwise to home
      if (fromPayment) {
        // Instead of using history.push, use window.location to force a full page refresh
        window.location.href = '/credits?payment_status=success&from_login=true';
      } else {
        history.push('/');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%'
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Sign In
          </Typography>
          
          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mt: 2, 
                width: '100%',
                mb: 2
              }}
              onClose={() => setSuccess('')}
            >
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {success}
              </Typography>
              {success.includes('Payment initiated') && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  After logging in, you'll be redirected to your credits page where you can check the status of your payment.
                </Typography>
              )}
            </Alert>
          )}
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2, 
                width: '100%',
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
              onClose={() => setError('')}
            >
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {error.split(': ')[0]}
              </Typography>
              {error.includes(': ') && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {error.split(': ')[1]}
                </Typography>
              )}
              {error.toLowerCase().includes('locked') && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  This is a security measure to protect your account.
                </Typography>
              )}
              {error.toLowerCase().includes('disabled') && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  If you believe this is a mistake, please contact our support team.
                </Typography>
              )}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={formData.username}
              onChange={handleChange}
              disabled={isLoading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={isLoading}
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  color="primary"
                  disabled={isLoading}
                />
              }
              label="Remember me"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/register" variant="body2">
                {"Don't have an account? Sign Up"}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default Login; 